import React, { useState, useRef, useEffect } from 'react';
import { User, LogOut, Settings, Shield, ChevronRight, Eye } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthContext } from '@/auth/AuthProvider';
import { useAuthValidation } from '@/hooks/useAuthValidation';
import { tokenState } from '@/auth/tokenState';
import { logout } from '@/auth/authFunctions';
import { createPortal } from 'react-dom';

interface ProfileMenuProps {
  className?: string;
}

export function ProfileMenu({ className }: ProfileMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [popupPosition, setPopupPosition] = useState({ bottom: 0, left: 0 });
  const { isLoggedIn } = useAuthContext();
  const { user: authUser, isLoading } = useAuthValidation();
  
  // Get user data from auth validation or token state
  const tokenUser = tokenState.getUserData();
  const user = authUser || tokenUser;

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPopupPosition({
        bottom: window.innerHeight - rect.top + 8,
        left: rect.left
      });
    }
  }, [isOpen]);

  // Update token state with complete user data from auth validation
  useEffect(() => {
    if (authUser && tokenUser) {
      const updatedUser = {
        ...tokenUser,
        ...authUser
      };
      tokenState.setUserData(updatedUser);
    }
  }, [authUser, tokenUser]);

  if (!isLoggedIn || !user) return null;

  // Get user initials
  const getInitials = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    return user?.username?.slice(0, 2).toUpperCase() || 'U';
  };

  // Get display name
  const getDisplayName = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user?.username || 'User';
  };

  // Get user role - handle both primary_site_role and role fields (matching UserManagement logic)
  const getUserRole = () => {
    // Check primary_site_role first, then fall back to role field
    const role = user?.primary_site_role || user?.role || 'viewer';
    
    console.log('getUserRole - user:', user);
    console.log('getUserRole - primary_site_role:', user?.primary_site_role);
    console.log('getUserRole - role:', user?.role);
    console.log('getUserRole - final role value:', role);
    
    // Map any backend role to our 3 simplified roles
    if (role === 'admin' || role === 'superuser' || role === 'system_admin') return 'admin';
    if (role === 'operator' || role === 'manager' || role === 'facilities_manager' || role === 'building_engineer') return 'operator';
    return 'viewer';
  };

  // Get role display using the getRoleName function from types
  const getRoleDisplay = () => {
    const role = getUserRole();
    // Using the same logic as in user.ts
    const roleDefinitions = {
      admin: 'Administrator',
      operator: 'Operator',
      viewer: 'Viewer'
    };
    return roleDefinitions[role] || 'Unknown';
  };

  // Check if user is admin
  const isAdmin = () => {
    return getUserRole() === 'admin';
  };

  // Role configuration matching UserManagement
  const roleConfig = {
    admin: { bgColor: 'bg-blue-50', textColor: 'text-dark-blue' },
    operator: { bgColor: 'bg-green/10', textColor: 'text-green' },
    viewer: { bgColor: 'bg-gray-100', textColor: 'text-dark-grey' }
  };

  const role = getUserRole();
  const config = roleConfig[role];

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <div className={cn("relative flex justify-center", className)}>
      {/* Profile Button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "w-9 h-9 rounded-lg flex items-center justify-center font-medium text-sm hover:shadow-sm transition-all duration-200",
          config.bgColor,
          config.textColor
        )}
      >
        {getInitials()}
      </button>

      {/* Profile Popup - Rendered via Portal */}
      {isOpen && createPortal(
        <>
          {/* Backdrop - Full screen */}
          <div 
            className="fixed inset-0 z-[9998]" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Popup Content */}
          <div 
            className="fixed w-80 bg-white rounded-2xl shadow-xl border border-light-grey/50 z-[9999] overflow-hidden"
            style={{
              bottom: `${popupPosition.bottom}px`,
              left: `${popupPosition.left}px`
            }}
          >
            {/* Header */}
            <div className="p-6 bg-white">
              <div className="flex items-center gap-4">
                <div className={cn(
                  "w-16 h-16 rounded-xl flex items-center justify-center font-semibold text-lg",
                  config.bgColor,
                  config.textColor
                )}>
                  {getInitials()}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="heading-6 text-black-blue truncate">
                    {getDisplayName()}
                  </h3>
                  <p className="paragraph-14 text-dark-grey truncate mt-1">
                    {user?.email}
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    {/* Role badge matching UserManagement style */}
                    <div className={cn(
                      "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md",
                      config.bgColor
                    )}>
                      {role === 'admin' && <Shield className="w-4 h-4" />}
                      {role === 'operator' && <Settings className="w-4 h-4" />}
                      {role === 'viewer' && <Eye className="w-4 h-4" />}
                      <span className={cn("paragraph-12 font-medium", config.textColor)}>
                        {getRoleDisplay()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* User Details */}
            {user?.sites && user.sites.length > 0 && (
              <div className="px-6 pb-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="paragraph-14 text-dark-grey font-medium">Your Sites</span>
                  <span className="paragraph-12 text-dark-grey/70">({user.sites.length})</span>
                </div>
                <div className="space-y-2">
                  {user.sites.map((site) => (
                    <div key={site.site_id} className="flex items-center justify-between py-2 px-3 bg-light-grey/20 rounded-lg">
                      <span className="paragraph-14 text-black-blue">
                        {site.site_name}
                        {site.is_primary && (
                          <span className="ml-2 paragraph-12 text-primary">(Primary)</span>
                        )}
                      </span>
                      <span className={cn(
                        "paragraph-12 px-3 py-1 rounded-md font-medium",
                        site.role === 'admin' ? 'bg-blue-50 text-dark-blue' :
                        site.role === 'operator' ? 'bg-green/10 text-green' :
                        'bg-gray-100 text-dark-grey'
                      )}>
                        {site.role}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="border-t border-light-grey/50">
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-between px-6 py-4 text-danger hover:bg-danger/5 transition-all"
              >
                <div className="flex items-center gap-3">
                  <LogOut className="w-5 h-5" />
                  <span className="paragraph-14 font-medium">Sign Out</span>
                </div>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}